import { createSelector } from "reselect";

import { getCurrentSale } from "./currentSale";
import { selectTradingTermIdsByBillingSettingsIdLookup } from "./indexes";
import { createLookupCombiner, createLookupSelectors } from "./lib";
import { getBusinesses, getTradingTerms } from "./root";
import { bestFitForSale } from "lib/sale";

export const [
  selectDeploymentTradingTermsByBillingSettingsIdLookup,
  getDeploymentTradingTermsByBillingSettingsId,
] = createLookupSelectors(
  [selectTradingTermIdsByBillingSettingsIdLookup, getTradingTerms],
  createLookupCombiner(
    (
      tradingTermsIds: string[],
      tradingTermsByIdLookup: StateModelById<TradingTerm>,
    ) =>
      tradingTermsIds
        .map(tradingTermId => tradingTermsByIdLookup[tradingTermId])
        .filter(term => !term.businessId), // Only include terms with no businessId
  ),
);

export const getCurrentDefaultTradingTerms = createSelector(
  [getCurrentSale, getTradingTerms],
  (
    currentSale: Sale | null,
    tradingTermsLookup: StateModelById<TradingTerm>,
  ) => {
    // Get the relevant trading terms for this sale.
    return currentSale
      ? bestFitForSale(currentSale, Object.values(tradingTermsLookup))
      : null;
  },
);

export const getBusinessTradingTermsById = createSelector(
  [getBusinesses, getTradingTerms],
  (
    businesses: StateModelById<Business>,
    tradingTermsLookup: StateModelById<TradingTerm>,
  ) => {
    return Object.values(businesses).reduce((acc, { id: businessId }) => {
      acc[businessId] = Object.values(tradingTermsLookup).filter(
        term => term.businessId === businessId,
      );
      return acc;
    }, {});
  },
);

export const selectBusinessTradingTermsByIdLookup = createSelector(
  [getBusinessTradingTermsById],
  (businessTradingTermsById: Record<string, TradingTerm>) => {
    return Object.keys(businessTradingTermsById).reduce((acc, businessId) => {
      acc[businessId] = businessTradingTermsById[businessId];
      return acc;
    }, {});
  },
);
